import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map, catchError, finalize, share } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GracePeriodFeature } from '@core/models/feature.model';
import { OrgScopedService } from '@core/interfaces/org-scoped-service.interface';

interface FeaturesResponse {
  plan: string;
  features: Record<string, boolean>;
  gracePeriod?: Record<string, GracePeriodFeature>;
}

/**
 * Signal-based service that loads and caches the feature map for the
 * authenticated organization. Provides reactive access to feature flags
 * for conditional rendering and route guarding.
 *
 * On load failure the map stays unloaded (fail-closed) but the failure is NOT
 * latched: the next `loadFeatures()` (e.g. the `featureGuard` on the next
 * navigation) re-fetches, so a transient backend error never blocks the
 * feature-gated navigation permanently. Concurrent calls share one request.
 */
@Injectable({ providedIn: 'root' })
export class FeatureService implements OrgScopedService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.authAPI}/features`;
  private readonly superAdmin = signal(false);

  private readonly features = signal<Record<string, boolean>>({});
  private readonly currentPlan = signal<string>('basic');
  private readonly featuresLoaded = signal<boolean>(false);
  private readonly gracePeriodMap = signal<Record<string, GracePeriodFeature>>({});
  private sessionCleared = false;
  /** In-flight fetch shared by concurrent `loadFeatures()` callers. */
  private inflight$: Observable<void> | null = null;

  /** Read-only snapshot of the current feature map. */
  readonly featureMap = this.features.asReadonly();

  /** Current plan identifier (e.g. 'basic', 'pro', 'enterprise'). */
  readonly plan = this.currentPlan.asReadonly();

  /** Whether the features have been loaded at least once. */
  readonly loaded = this.featuresLoaded.asReadonly();

  /** Read-only snapshot of features currently in grace period. */
  readonly gracePeriod = this.gracePeriodMap.asReadonly();

  /** Whether any feature is currently in grace period. */
  readonly hasGracePeriod = computed(() => Object.keys(this.gracePeriodMap()).length > 0);

  /** Days remaining until the nearest grace period expires. */
  readonly gracePeriodDaysLeft = computed(() => {
    const entries = Object.values(this.gracePeriodMap());
    if (entries.length === 0) return 0;
    const nearest = Math.min(...entries.map(e => new Date(e.expiresAt).getTime()));
    return Math.max(0, Math.ceil((nearest - Date.now()) / 86_400_000));
  });

  /**
   * Sets the super_admin bypass flag. Called by AuthService after login.
   * @param value `true` to bypass all feature checks (super admin).
   * @returns void
   */
  setSuperAdmin(value: boolean): void {
    this.superAdmin.set(value);
    this.sessionCleared = false;
  }

  /**
   * Fetches the feature map from the BFF and caches it in signals.
   * Skips the request if the session was cleared (logout). Concurrent calls
   * share a single in-flight request. On error the observable completes
   * without latching `loaded`, so the next call re-fetches (fail-closed with
   * retry on the next navigation instead of a permanent lockout).
   * @returns An observable that completes once the feature map is cached.
   */
  loadFeatures(): Observable<void> {
    if (this.sessionCleared) return of(undefined);
    if (this.inflight$) return this.inflight$;
    this.inflight$ = this.http.get<FeaturesResponse>(`${this.apiUrl}/me`).pipe(
      tap(response => {
        // Only emit a new signal value when the data actually changed. The layout
        // re-fetches features on every NavigationEnd; setting a fresh object each
        // time churns `featureMap()` and re-fires downstream effects (e.g. the
        // sidenav nav-sync effect), which would stomp a group the user just
        // expanded. Equality guards keep the signals stable when nothing changed.
        const features = response.features;
        if (!this.shallowEqualBool(this.features(), features)) {
          this.features.set(features);
        }
        if (this.currentPlan() !== response.plan) {
          this.currentPlan.set(response.plan);
        }
        const grace = response.gracePeriod ?? {};
        if (!this.graceEqual(this.gracePeriodMap(), grace)) {
          this.gracePeriodMap.set(grace);
        }
        if (!this.featuresLoaded()) {
          this.featuresLoaded.set(true);
        }
      }),
      map(() => undefined),
      // A failed fetch must NOT mark the map as loaded: `featureGuard` would
      // then trust an empty map and block every feature-gated route until a
      // full page reload. Completing without latching lets the next
      // navigation retry the fetch.
      catchError(() => of(undefined)),
      finalize(() => {
        this.inflight$ = null;
      }),
      share(),
    );
    return this.inflight$;
  }

  /**
   * Shallow equality for the `Record<string, boolean>` feature map.
   * @param a First feature map.
   * @param b Second feature map.
   * @returns `true` when both maps have the same keys and boolean values.
   */
  private shallowEqualBool(a: Record<string, boolean>, b: Record<string, boolean>): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(key => a[key] === b[key]);
  }

  /**
   * Equality for the grace-period map (compares keys + expiry timestamps).
   * @param a First grace-period map.
   * @param b Second grace-period map.
   * @returns `true` when both maps have the same keys and expiry timestamps.
   */
  private graceEqual(
    a: Record<string, GracePeriodFeature>,
    b: Record<string, GracePeriodFeature>,
  ): boolean {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(key => b[key] != null && a[key].expiresAt === b[key].expiresAt);
  }

  /**
   * Returns whether a single feature is enabled for the current org.
   * Returns `false` if features have not loaded yet or the key is absent.
   * The `featureGuard` waits for features to load before calling this method.
   * @param featureId Feature flag identifier.
   * @returns `true` when the feature is enabled (or the user is super admin).
   */
  isEnabled(featureId: string): boolean {
    if (this.superAdmin()) return true;
    const map = this.features();
    if (!this.featuresLoaded()) return false;
    if (!(featureId in map)) return false;
    return map[featureId];
  }

  /**
   * Returns `true` if at least one of the given feature IDs is enabled.
   * @param featureIds Feature flag identifiers to test.
   * @returns `true` when any of the features is enabled.
   */
  anyEnabled(featureIds: string[]): boolean {
    return featureIds.some(id => this.isEnabled(id));
  }

  /**
   * Whether a feature is in grace period (enabled but soon to be disabled).
   * @param featureId Feature flag identifier.
   * @returns `true` when the feature is in grace period.
   */
  isInGracePeriod(featureId: string): boolean {
    return featureId in this.gracePeriodMap();
  }

  /**
   * Grace period expiry date for a feature.
   * @param featureId Feature flag identifier.
   * @returns The expiry `Date`, or `null` when the feature is not in grace period.
   */
  gracePeriodExpiry(featureId: string): Date | null {
    const info = this.gracePeriodMap()[featureId];
    return info ? new Date(info.expiresAt) : null;
  }

  /**
   * Clears the cached feature map. Should be called on logout.
   * @returns void
   */
  clear(): void {
    this.features.set({});
    this.currentPlan.set('basic');
    this.featuresLoaded.set(false);
    this.superAdmin.set(false);
    this.gracePeriodMap.set({});
    this.sessionCleared = true;
  }

  /**
   * Clears cached feature state without ending the session, so that the next
   * `loadFeatures()` re-fetches for the current organization.
   *
   * AI-17 F2 — `OrgScopedService.invalidate()`. Idempotent. Does NOT trigger
   * a fetch synchronously; the consumer (component effect in F3 or natural
   * route reload) is responsible for the re-fetch.
   * @returns void
   */
  invalidate(): void {
    this.features.set({});
    this.currentPlan.set('basic');
    this.featuresLoaded.set(false);
    this.gracePeriodMap.set({});
  }
}
